import React, { useState, useEffect } from "react"
import { Col, Row, Spinner } from "reactstrap"
import { Link } from "react-router-dom"
import api from "API/Api"
import { usePermissions } from "API/PermissionContext"

const PaymentDashboard = () => {
  const { canApprove, canEdit, canExport, MODULE } = usePermissions()
  const MID = MODULE.PAYMENTS

  const [loading, setLoading] = useState(true)
  const [stats, setStats] = useState(null)
  const [batches, setBatches] = useState([])
  const [trend, setTrend] = useState([])

  useEffect(() => { loadDashboard() }, [])

  const loadDashboard = async () => {
    try {
      const resp = await api.get("payment-ledger/dashboard")
      if (resp.data.success) {
        setStats(resp.data.data.stats)
        setBatches(resp.data.data.recent_batches || [])
        setTrend(resp.data.data.trend || [])
      }
    } catch (e) { console.error("Dashboard load failed:", e) }
    setLoading(false)
  }

  const statusBadge = (status) => {
    const map = {
      draft: "bg-secondary", pending_approval: "bg-warning", approved: "bg-info",
      processing: "bg-primary", completed: "bg-success", partial: "bg-warning", failed: "bg-danger",
    }
    return <span className={`badge ${map[status] || "bg-secondary"}`}>{status?.replace(/_/g, " ").toUpperCase()}</span>
  }

  const cardStyle = (color) => ({
    background: `linear-gradient(135deg, ${color}15, ${color}08)`,
    borderLeft: `4px solid ${color}`,
    borderRadius: "12px", padding: "20px", height: "100%",
    boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
    transition: "transform 0.2s, box-shadow 0.2s",
  })

  const maxTrend = Math.max(...trend.map(t => t.amount), 1)

  if (loading) return <div style={{textAlign:"center",padding:"60px"}}><Spinner color="primary" /><p style={{marginTop:"10px",color:"#6c757d"}}>Loading payment dashboard...</p></div>

  return (
    <div className="dashBody">
      <div className="font-size-18 mb-0 dashHeader">
        <Row>
          <Col md={6}><h4><i className="fas fa-money-check-alt me-2" style={{color:"#6366f1"}}></i>Payment Dashboard</h4></Col>
          <Col md={6} style={{textAlign:"right"}}>
            <Link className="dashBtn" to="/payment-process" style={{marginRight:"8px"}}><i className="fas fa-play-circle"></i> <b>Process Payment</b></Link>
            <Link className="dashBtn" to="/payment-config"><i className="fas fa-cog"></i> <b>Settings</b></Link>
          </Col>
        </Row>
      </div>
      <div className="dashContent">

        {/* ── Summary Cards ── */}
        <Row className="mb-4">
          {[
            {label:"Pending Payments",value:"₹"+(stats?.total_pending ? Number(stats.total_pending).toLocaleString("en-IN",{minimumFractionDigits:2}) : "0.00"),count:stats?.pending_count||0,icon:"fas fa-clock",color:"#f59e0b"},
            {label:"Processing",value:"₹"+(stats?.total_processing ? Number(stats.total_processing).toLocaleString("en-IN",{minimumFractionDigits:2}) : "0.00"),count:stats?.processing_count||0,icon:"fas fa-spinner",color:"#3b82f6"},
            {label:"Paid This Month",value:"₹"+(stats?.total_paid_this_month ? Number(stats.total_paid_this_month).toLocaleString("en-IN",{minimumFractionDigits:2}) : "0.00"),count:stats?.paid_count_this_month||0,icon:"fas fa-check-circle",color:"#22c55e"},
            {label:"Held / Issues",value:"₹"+(stats?.total_held ? Number(stats.total_held).toLocaleString("en-IN",{minimumFractionDigits:2}) : "0.00"),count:stats?.held_count||0,icon:"fas fa-exclamation-triangle",color:"#ef4444"},
          ].map((c, i) => (
            <Col key={i} md={3} sm={6} className="mb-3">
              <div style={cardStyle(c.color)} className="payment-card">
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
                  <div>
                    <div style={{fontSize:"11px",color:"#6c757d",textTransform:"uppercase",letterSpacing:"1px",fontWeight:"600"}}>{c.label}</div>
                    <div style={{fontSize:"22px",fontWeight:"700",color:"#1e293b",marginTop:"4px"}}>{c.value}</div>
                    <div style={{fontSize:"12px",color:c.color,fontWeight:"500",marginTop:"2px"}}>{c.count} entries</div>
                  </div>
                  <i className={c.icon} style={{fontSize:"28px",color:c.color,opacity:0.6}}></i>
                </div>
              </div>
            </Col>
          ))}
        </Row>

        {/* ── Monthly Trend ── */}
        {trend.length > 0 && (
          <div style={{background:"#fff",borderRadius:"12px",padding:"20px",marginBottom:"24px",boxShadow:"0 2px 8px rgba(0,0,0,0.06)"}}>
            <h6 style={{color:"#334155",marginBottom:"16px",fontWeight:"600"}}><i className="fas fa-chart-bar me-2"></i>Payment Trend (Last 6 Months)</h6>
            <div style={{display:"flex",alignItems:"flex-end",gap:"12px",height:"120px",padding:"0 10px"}}>
              {trend.map((t, i) => (
                <div key={i} style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center"}}>
                  <div style={{fontSize:"10px",color:"#6c757d",marginBottom:"4px",fontWeight:"600"}}>₹{Number(t.amount||0).toLocaleString("en-IN")}</div>
                  <div style={{
                    width:"100%", maxWidth:"60px",
                    height: Math.max(4, (t.amount / maxTrend) * 80) + "px",
                    background:`linear-gradient(180deg, #6366f1, #818cf8)`,
                    borderRadius:"6px 6px 0 0",
                    transition:"height 0.5s ease",
                  }}></div>
                  <div style={{fontSize:"10px",color:"#6c757d",marginTop:"6px",fontWeight:"500"}}>{t.month}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── Recent Batches ── */}
        <div style={{background:"#fff",borderRadius:"12px",padding:"20px",boxShadow:"0 2px 8px rgba(0,0,0,0.06)"}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:"16px"}}>
            <h6 style={{color:"#334155",fontWeight:"600",margin:0}}><i className="fas fa-layer-group me-2"></i>Recent Payment Batches</h6>
            <Link to="/payment-process" style={{fontSize:"13px",color:"#6366f1",fontWeight:"500"}}>View All →</Link>
          </div>

          {batches.length === 0 ? (
            <div style={{textAlign:"center",padding:"40px",color:"#94a3b8"}}>
              <i className="fas fa-inbox" style={{fontSize:"40px",marginBottom:"12px"}}></i>
              <p>No payment batches yet</p>
              <Link to="/payment-process" className="btn btn-primary btn-sm"><i className="fas fa-plus me-1"></i>Generate First Payment</Link>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table mb-0">
                <thead>
                  <tr style={{fontSize:"12px",color:"#6c757d",textTransform:"uppercase",letterSpacing:"0.5px"}}>
                    <th>Batch Code</th><th>Period</th><th>Cycle</th><th>Payees</th>
                    <th>Gross (₹)</th><th>Deductions (₹)</th><th>Net (₹)</th>
                    <th>Bank File</th><th>Status</th><th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {batches.map((b, i) => (
                    <tr key={b.id||i} style={{fontSize:"13px"}}>
                      <td><span style={{fontWeight:"600",color:"#6366f1"}}>{b.batch_code}</span></td>
                      <td style={{whiteSpace:"nowrap"}}>{b.period_from} → {b.period_to}</td>
                      <td><span className="badge bg-outline-secondary" style={{border:"1px solid #94a3b8",color:"#64748b",background:"transparent"}}>{b.billing_cycle}</span></td>
                      <td style={{fontWeight:"600"}}>{b.total_payees}</td>
                      <td style={{color:"#0284c7"}}>₹{Number(b.total_gross||0).toLocaleString("en-IN",{minimumFractionDigits:2})}</td>
                      <td style={{color:"#dc2626"}}>₹{Number(b.total_deductions||0).toLocaleString("en-IN",{minimumFractionDigits:2})}</td>
                      <td style={{fontWeight:"700",color:"#059669"}}>₹{Number(b.total_net||0).toLocaleString("en-IN",{minimumFractionDigits:2})}</td>
                      <td>{b.bank_file_generated ? (
                        <a href={`${api.defaults.baseURL}/payment-batch/${b.id}/bank-file`} target="_blank" rel="noreferrer" className="btn btn-sm btn-outline-success" style={{fontSize:"11px"}}>
                          <i className="fas fa-download me-1"></i>CSV
                        </a>
                      ) : <span style={{color:"#94a3b8",fontSize:"11px"}}>—</span>}</td>
                      <td>{statusBadge(b.status)}</td>
                      <td>
                        <Link to={`/payment-process?batch=${b.id}`} className="btn btn-sm btn-outline-primary" style={{fontSize:"11px"}}><i className="fas fa-eye"></i></Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default PaymentDashboard
